import type {
  CustomerTimelineCallEntry,
  CustomerCallMember,
} from "@repo/app-types/entities/performanceCustomer";
import { CustomerTimelineRowDetails } from "@repo/ui/customerTimeline/customerTimelineRowDetails";
import { Size } from "@repo/ui/helpers/helpers";
import { OutcomeResultPill } from "@repo/ui/Pills/outcomeResultPill";
import { IconPhone } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { renderCallLength } from "../../teammatePerformance/durationFormatting.js";
import { GradingSectionSummariesContainer } from "../../teammatePerformance/gradingSectionSummariesContainer.js";
import {
  buildCustomerTimelineTeammateLabel,
  renderTrailingText,
  type CustomerTimelineRowOptions,
  type InteractionRowParts,
} from "./timelineRowHelpers.js";

/** A call with a recording, with a row per teammate graded on it. */
export function renderCallRow(
  entry: CustomerTimelineCallEntry,
  options: CustomerTimelineRowOptions,
): InteractionRowParts {
  const { openInteraction, viewedTeammateId } = options;
  // The viewed teammate's recording comes first.
  const members = [
    ...entry.members.filter(
      (member) => member.teammate.id === viewedTeammateId,
    ),
    ...entry.members.filter(
      (member) => member.teammate.id !== viewedTeammateId,
    ),
  ];
  return {
    icon: <IconPhone className="size-4" />,
    details: (
      <CustomerTimelineRowDetails
        title={entry.title || "Call recording"}
        onOpen={(recordingId) => {
          const member = members.find(
            (candidate) => candidate.recording_id === recordingId,
          );
          if (member)
            openInteraction(member.grading_id, {
              recordingId: member.recording_id,
            });
        }}
        participants={members.map((member) => ({
          ...buildCustomerTimelineTeammateLabel(member.teammate),
          id: member.recording_id,
          gradingSummary: renderSplitGradingSummary(member),
          outcome: member.outcome ? (
            <OutcomeResultPill size={Size.SMALL} result={member.outcome} />
          ) : undefined,
        }))}
        trailingAccessory={
          entry.duration_seconds != null &&
          renderTrailingText(renderCallLength(entry.duration_seconds))
        }
      />
    ),
  };
}

function renderSplitGradingSummary(member: CustomerCallMember): ReactNode {
  if (!member.is_split) return undefined;
  if (!member.assessment) return "No score available";
  return (
    <GradingSectionSummariesContainer
      assessment={member.assessment}
      emptyState="Scoring not applicable"
    />
  );
}
